import { useState, useMemo, useCallback } from 'react';

// Camelot wheel segments in clockwise order
const CAMELOT_SEGMENTS = [
  { code: '1A', key: 'A♭m', angle: 0, color: '#22c55e' },
  { code: '2A', key: 'E♭m', angle: 30, color: '#16a34a' },
  { code: '3A', key: 'B♭m', angle: 60, color: '#15803d' },
  { code: '4A', key: 'Fm', angle: 90, color: '#166534' },
  { code: '5A', key: 'Cm', angle: 120, color: '#14532d' },
  { code: '6A', key: 'Gm', angle: 150, color: '#0f766e' },
  { code: '7A', key: 'Dm', angle: 180, color: '#0d9488' },
  { code: '8A', key: 'Am', angle: 210, color: '#0891b2' },
  { code: '9A', key: 'Em', angle: 240, color: '#0284c7' },
  { code: '10A', key: 'Bm', angle: 270, color: '#2563eb' },
  { code: '11A', key: 'F#m', angle: 300, color: '#4f46e5' },
  { code: '12A', key: 'C#m', angle: 330, color: '#7c3aed' },
  { code: '1B', key: 'B', angle: 0, color: '#84cc16' },
  { code: '2B', key: 'G♭', angle: 30, color: '#65a30d' },
  { code: '3B', key: 'D♭', angle: 60, color: '#4d7c0f' },
  { code: '4B', key: 'A♭', angle: 90, color: '#3f6212' },
  { code: '5B', key: 'E♭', angle: 120, color: '#365314' },
  { code: '6B', key: 'B♭', angle: 150, color: '#14b8a6' },
  { code: '7B', key: 'F', angle: 180, color: '#2dd4bf' },
  { code: '8B', key: 'C', angle: 210, color: '#22d3ee' },
  { code: '9B', key: 'G', angle: 240, color: '#38bdf8' },
  { code: '10B', key: 'D', angle: 270, color: '#60a5fa' },
  { code: '11B', key: 'A', angle: 300, color: '#818cf8' },
  { code: '12B', key: 'E', angle: 330, color: '#a78bfa' },
];

// Get compatible keys for a given Camelot code
function getCompatibleKeys(code) {
  if (!code) return [];
  
  const number = parseInt(code.slice(0, -1));
  const letter = code.slice(-1);
  
  // Same key
  const same = code;
  
  // +1 semitone (clockwise neighbor)
  const next = `${number === 12 ? 1 : number + 1}${letter}`;
  
  // -1 semitone (counterclockwise neighbor)
  const prev = `${number === 1 ? 12 : number - 1}${letter}`;
  
  // Relative major/minor (same position, different letter)
  const relative = `${number}${letter === 'A' ? 'B' : 'A'}`;
  
  return [same, next, prev, relative];
}

export default function CamelotWheel({ 
  selectedKey, 
  onSelectKey, 
  trackCounts = {},
  size = 400 
}) {
  const [hoveredKey, setHoveredKey] = useState(null);
  
  const compatibleKeys = useMemo(() => {
    const key = hoveredKey || selectedKey;
    return getCompatibleKeys(key);
  }, [hoveredKey, selectedKey]);

  // Memoize handlers to prevent unnecessary re-renders
  const handleMouseEnter = useCallback((code) => {
    setHoveredKey(code);
  }, []);
  
  const handleMouseLeave = useCallback(() => {
    setHoveredKey(null);
  }, []);
  
  const handleClick = useCallback((code) => {
    if (onSelectKey) {
      onSelectKey(code === selectedKey ? null : code);
    }
  }, [onSelectKey, selectedKey]);

  const centerX = size / 2;
  const centerY = size / 2;
  const outerRadius = size / 2 - 10;
  const innerRadius = outerRadius * 0.55;
  const middleRadius = (outerRadius + innerRadius) / 2;

  // Create SVG arc path for a segment
  const createArcPath = (startAngle, endAngle, inner, outer) => {
    const startRad = ((startAngle - 90) * Math.PI) / 180;
    const endRad = ((endAngle - 90) * Math.PI) / 180;
    
    const x1 = centerX + outer * Math.cos(startRad);
    const y1 = centerY + outer * Math.sin(startRad);
    const x2 = centerX + outer * Math.cos(endRad);
    const y2 = centerY + outer * Math.sin(endRad);
    const x3 = centerX + inner * Math.cos(endRad);
    const y3 = centerY + inner * Math.sin(endRad);
    const x4 = centerX + inner * Math.cos(startRad);
    const y4 = centerY + inner * Math.sin(startRad);
    
    const largeArc = endAngle - startAngle > 180 ? 1 : 0;
    
    return `M ${x1} ${y1} A ${outer} ${outer} 0 ${largeArc} 1 ${x2} ${y2} L ${x3} ${y3} A ${inner} ${inner} 0 ${largeArc} 0 ${x4} ${y4} Z`;
  };

  // Get text position for a segment
  const getTextPosition = (angle, radius) => {
    const rad = ((angle + 15 - 90) * Math.PI) / 180;
    return {
      x: centerX + radius * Math.cos(rad),
      y: centerY + radius * Math.sin(rad),
    };
  };

  // Separate A (minor) and B (major) segments
  const minorSegments = CAMELOT_SEGMENTS.filter(s => s.code.endsWith('A'));
  const majorSegments = CAMELOT_SEGMENTS.filter(s => s.code.endsWith('B'));

  const renderSegment = (segment, isOuter) => {
    const startAngle = segment.angle;
    const endAngle = segment.angle + 30;
    const inner = isOuter ? middleRadius + 2 : innerRadius;
    const outer = isOuter ? outerRadius : middleRadius - 2;
    const textRadius = (outer + inner) / 2;
    const textPos = getTextPosition(segment.angle, textRadius);
    
    const isSelected = selectedKey === segment.code;
    const isCompatible = compatibleKeys.includes(segment.code);
    const isHovered = hoveredKey === segment.code;
    const count = trackCounts[segment.code] || 0;
    
    let opacity = 0.4;
    let strokeWidth = 0;
    let strokeColor = 'transparent';
    
    if (isSelected) {
      opacity = 1;
      strokeWidth = 3;
      strokeColor = '#fff';
    } else if (isCompatible && (hoveredKey || selectedKey)) {
      opacity = 0.85;
      strokeWidth = 2;
      strokeColor = '#3B82F6';
    } else if (isHovered) {
      opacity = 0.9;
    }
    
    return (
      <g key={segment.code} style={{ willChange: 'opacity' }}>
        <path
          d={createArcPath(startAngle, endAngle, inner, outer)}
          fill={segment.color}
          fillOpacity={opacity}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
          className="cursor-pointer"
          style={{ 
            transition: 'fill-opacity 0.15s ease-out, stroke 0.15s ease-out, stroke-width 0.15s ease-out',
            willChange: 'fill-opacity, stroke'
          }}
          onMouseEnter={() => handleMouseEnter(segment.code)}
          onMouseLeave={handleMouseLeave}
          onClick={() => handleClick(segment.code)}
          data-testid={`camelot-segment-${segment.code}`}
        />
        <text
          x={textPos.x}
          y={textPos.y}
          textAnchor="middle"
          dominantBaseline="middle"
          className="font-mono text-xs font-bold pointer-events-none select-none"
          fill={isSelected || isCompatible ? '#fff' : '#9ca3af'}
          style={{ transition: 'fill 0.15s ease-out' }}
        >
          {segment.code}
        </text>
        {count > 0 && (
          <text
            x={textPos.x}
            y={textPos.y + 12}
            textAnchor="middle"
            dominantBaseline="middle"
            className="font-mono text-[10px] pointer-events-none select-none"
            fill="#6b7280"
          >
            ({count})
          </text>
        )}
      </g>
    );
  };

  return (
    <div className="relative" style={{ contain: 'layout style' }}>
      <svg 
        width={size} 
        height={size} 
        className="mx-auto"
        style={{ shapeRendering: 'geometricPrecision' }}
      >
        {/* Background circle */}
        <circle
          cx={centerX}
          cy={centerY}
          r={outerRadius}
          fill="#FFFFFF"
          stroke="#1f1f1f"
          strokeWidth={1}
        />
        
        {/* Inner circle background */}
        <circle
          cx={centerX}
          cy={centerY}
          r={innerRadius - 5}
          fill="#FFFFFF"
        />
        
        {/* Major keys (outer ring - B) */}
        {majorSegments.map(segment => renderSegment(segment, true))}
        
        {/* Minor keys (inner ring - A) */}
        {minorSegments.map(segment => renderSegment(segment, false))}
        
        {/* Center text */}
        <text
          x={centerX}
          y={centerY - 10}
          textAnchor="middle"
          className="font-heading text-lg font-bold"
          fill="#3B82F6"
        >
          CAMELOT
        </text>
        <text
          x={centerX}
          y={centerY + 10}
          textAnchor="middle"
          className="font-mono text-xs"
          fill="#6b7280"
        >
          {selectedKey ? `Selected: ${selectedKey}` : 'Click to filter'}
        </text>
      </svg>
      
      {/* Legend */}
      <div className="flex justify-center gap-6 mt-4 text-xs font-mono">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 bg-green-500 rounded-full" />
          <span className="text-[#64748B]">Minor (A)</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 bg-lime-500 rounded-full" />
          <span className="text-[#64748B]">Major (B)</span>
        </div>
      </div>
      
      {/* Compatibility hint */}
      {(hoveredKey || selectedKey) && (
        <div className="text-center mt-3">
          <p className="text-[#64748B] text-xs font-mono">
            Compatible: {compatibleKeys.join(', ')}
          </p>
        </div>
      )}
    </div>
  );
}
